import { DocumentationArticle } from '../components/DocumentationArticle';

export const AccountRoles = () => {
  return (
    <DocumentationArticle
      title="Understanding Your Account Roles and Access"
      description="This guide explains how account roles work in EchoRatings so you understand who can access your account and what actions each person can take."
      timeToComplete="3-5 minutes"
      requirements={[
        'Access to your EchoRatings account',
        'Permission to manage users, if applicable',
      ]}
      sections={[
        {
          title: 'Section 1: Access Your Account Settings',
          content: (
            <>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Log in to your EchoRatings account.</li>
                <li>Open your account settings from the main navigation.</li>
                <li>
                  Go to the section related to users, team members, or access.
                </li>
              </ol>
              <p>This is where you can view who has access to your account.</p>
            </>
          ),
        },
        {
          title: 'Section 2: Understand Account Roles',
          content: (
            <>
              <p className="mb-4">
                EchoRatings uses roles to control what each user can see and do.
              </p>
              <p className="mb-2">Roles help ensure that:</p>
              <ul className="list-disc list-inside space-y-1 mb-4">
                <li>The right people have the right level of access</li>
                <li>Sensitive actions are limited to authorized users</li>
                <li>Team members can work without affecting account setup</li>
              </ul>
              <p>
                Each role is designed for a specific type of responsibility.
              </p>
            </>
          ),
        },
        {
          title: 'Section 3: Common Role Types',
          content: (
            <>
              <p className="mb-4">
                While role names may vary, you'll generally see roles that fall
                into these categories:
              </p>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-base-raisinBlack">
                    1. Account owners or administrators
                  </h4>
                  <p>
                    These users can manage settings, integrations, users, and
                    billing-related actions.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-base-raisinBlack">
                    2. Managers or editors
                  </h4>
                  <p>
                    These users can work with reviews and automations but may not
                    have access to all account settings.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-base-raisinBlack">
                    3. View-only users
                  </h4>
                  <p>These users can view information but cannot make changes.</p>
                </div>
              </div>
              <p className="mt-4">
                Use the role descriptions in your account to confirm exact
                permissions.
              </p>
            </>
          ),
        },
        {
          title: 'Section 4: Adding or Managing Users',
          content: (
            <>
              <p className="mb-4">If you have permission to manage access:</p>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Open the user or team section in settings.</li>
                <li>Add a new user using their email address.</li>
                <li>Assign the appropriate role.</li>
                <li>Save your changes.</li>
              </ol>
              <p>Access updates usually take effect immediately.</p>
            </>
          ),
        },
        {
          title: 'Section 5: Best Practices for Access Management',
          content: (
            <>
              <p className="mb-4">To keep your account organized:</p>
              <ol className="list-decimal list-inside space-y-2 mb-4">
                <li>Give each user only the access they need.</li>
                <li>Review your user list periodically.</li>
                <li>Remove access for users who no longer need it.</li>
              </ol>
              <p>
                This helps prevent accidental changes and keeps your account
                secure.
              </p>
            </>
          ),
        },
      ]}
      faq={[
        {
          question: "Can I change a user's role later?",
          answer:
            'Yes. You can update roles at any time from account settings if you have permission.',
        },
        {
          question: 'How many users can I add?',
          answer:
            'This depends on your account setup. Refer to your account settings for current limits.',
        },
        {
          question: "What if someone can't see a feature they need?",
          answer:
            'Check their role to make sure it includes access to that area.',
        },
      ]}
    />
  );
};
